import React, { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Form, Input, Select, Switch, Table, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { User, CreateUserDto, UpdateUserDto, UserListParams } from "../../types";
import { UserService, OrgService } from "../../services";
import { Org } from "../../types/org";
import ErrorHandler from "../../utils/errorHandler";
import {
  DrawerFooter,
  EmptyState,
  FilterChips,
  Icon,
  Pager,
  PersonCell,
  ROLE,
  StatusBadge,
  Surface,
  ToolbarGroup,
  VocabBadge,
  formatTime,
} from "../../components/ui";
import "./UserList.scss";

type RoleOption = { value: string; label: string };
type Query = { role: string; phone: string; page: number; limit: number };

const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      return document.execCommand("copy");
    } catch {
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
};

const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState<Query>({ role: "all", phone: "", page: 1, limit: 10 });
  const [phoneInput, setPhoneInput] = useState("");
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [roleCounts, setRoleCounts] = useState<Record<string, number>>({});
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  const loadUsers = useCallback(async (q: Query) => {
    setLoading(true);
    try {
      const params: UserListParams = { page: q.page, limit: q.limit };
      if (q.role !== "all") params.role = q.role as UserListParams["role"];
      if (q.phone) params.phone = q.phone;
      const response = await UserService.getUserList(params);
      if (response.code === 0 && response.data) {
        setUsers(response.data.list || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.msg || "获取用户列表失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 各角色数量：每个角色查一次 total，用于筛选胶囊
  const loadRoleCounts = useCallback(async (roleList: RoleOption[], phone: string) => {
    try {
      const keys = ["all", ...roleList.map((role) => role.value)];
      const results = await Promise.all(
        keys.map((key) =>
          UserService.getUserList({
            page: 1,
            limit: 1,
            ...(key === "all" ? {} : { role: key as UserListParams["role"] }),
            ...(phone ? { phone } : {}),
          }).catch(() => null)
        )
      );
      const counts: Record<string, number> = {};
      results.forEach((res, index) => {
        if (res && res.code === 0 && res.data) counts[keys[index]] = res.data.total || 0;
      });
      setRoleCounts(counts);
    } catch {
      /* 数量只是辅助信息，失败时不打扰用户 */
    }
  }, []);

  useEffect(() => {
    // 角色与组织各自独立加载，避免其中一个失败连带清空另一个下拉。
    const loadRoles = async () => {
      try {
        const roleRes = await UserService.getRolesList();
        const roleList = roleRes.code === 0 && roleRes.data ? roleRes.data.roles : [];
        setRoles(roleList);
        loadRoleCounts(roleList, "");
      } catch (error) {
        ErrorHandler.handleApiError(error);
      }
    };
    const loadOrgs = async () => {
      try {
        // limit 需在服务端分页上限（100）以内，否则会被拒绝。
        const orgRes = await OrgService.getOrgList({ limit: 100 });
        if (orgRes.code === 0 && orgRes.data) setOrgs(orgRes.data.data || []);
      } catch (error) {
        ErrorHandler.handleApiError(error);
      }
    };
    loadRoles();
    loadOrgs();
  }, [loadRoleCounts]);

  useEffect(() => {
    loadUsers(query);
  }, [query, loadUsers]);

  const applyPhone = (phone: string) => {
    const value = phone.trim();
    setQuery((prev) => ({ ...prev, phone: value, page: 1 }));
    loadRoleCounts(roles, value);
  };

  const resetFilters = () => {
    setPhoneInput("");
    setQuery((prev) => ({ ...prev, role: "all", phone: "", page: 1 }));
    loadRoleCounts(roles, "");
  };

  const openCreate = () => {
    setCurrentUser(null);
    form.resetFields();
    form.setFieldsValue({ role: "customer", isEnabled: true });
    setDrawerOpen(true);
  };

  const openEdit = async (user: User) => {
    try {
      const response = await UserService.getUserDetail(user.id);
      if (response.code === 0 && response.data) {
        setCurrentUser(response.data);
        form.resetFields();
        form.setFieldsValue(response.data);
        setDrawerOpen(true);
      } else {
        message.error(response.msg || "获取用户信息失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    }
  };

  const handleSave = async (values: CreateUserDto | UpdateUserDto) => {
    setSaving(true);
    try {
      const response = currentUser
        ? await UserService.updateUser(currentUser.id, values as UpdateUserDto)
        : await UserService.createUser(values as CreateUserDto);
      if (response.code === 0) {
        message.success(currentUser ? "用户更新成功" : "用户创建成功");
        setDrawerOpen(false);
        loadUsers(query);
        if (!currentUser) loadRoleCounts(roles, query.phone);
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (identifier: string) => {
    const ok = await copyText(identifier);
    if (ok) message.success("邀请码已复制到剪切板");
    else message.error("复制失败，请手动复制");
  };

  const roleLabel = (value: string) => roles.find((role) => role.value === value)?.label || ROLE[value]?.[0] || value;

  const columns: ColumnsType<User> = [
    {
      title: "账号",
      key: "account",
      width: 240,
      render: (_, record) => <PersonCell name={record.username} sub={record.phone} tone={record.role === "admin" ? "ver" : "paper"} />,
    },
    {
      title: "角色",
      dataIndex: "role",
      key: "role",
      width: 130,
      render: (role: string) => <StatusBadge tone={ROLE[role]?.[1] || "ink"}>{roleLabel(role)}</StatusBadge>,
    },
    {
      title: "归属组织",
      dataIndex: "org",
      key: "org",
      render: (org: { name?: string } | undefined) => (org?.name ? org.name : <span className="text-faint">—</span>),
    },
    {
      title: "邀请码",
      dataIndex: "identifier",
      key: "identifier",
      width: 170,
      render: (identifier: string) =>
        identifier ? (
          <span className="user-list__code">
            <span className="mono">{identifier}</span>
            <Tooltip title="复制邀请码">
              <Button type="text" size="small" aria-label={`复制邀请码 ${identifier}`} icon={<Icon name="copy" size={14} />} onClick={() => handleCopy(identifier)} />
            </Tooltip>
          </span>
        ) : (
          <span className="text-faint">—</span>
        ),
    },
    {
      title: "状态",
      dataIndex: "isEnabled",
      key: "isEnabled",
      width: 110,
      render: (isEnabled: boolean) => <StatusBadge tone={isEnabled ? "sage" : "ink"}>{isEnabled ? "启用" : "已禁用"}</StatusBadge>,
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (date: string) => <span className="mono text-muted">{formatTime(date)}</span>,
    },
    {
      title: "操作",
      key: "action",
      width: 90,
      align: "right",
      render: (_, record) => (
        <button type="button" className="ddp-link" onClick={() => openEdit(record)}>
          编辑
        </button>
      ),
    },
  ];

  const chipItems = [
    { key: "all", label: "全部", count: roleCounts.all },
    ...roles.map((role) => ({ key: role.value, label: role.label, count: roleCounts[role.value] })),
  ];

  const filtered = query.role !== "all" || !!query.phone;

  return (
    <div className="user-list">
      <Surface
        toolbar={
          <>
            <ToolbarGroup>
              <FilterChips ariaLabel="按角色筛选" items={chipItems} value={query.role} onChange={(role) => setQuery((prev) => ({ ...prev, role, page: 1 }))} />
            </ToolbarGroup>
            <ToolbarGroup>
              <Input
                className="user-list__search"
                allowClear
                prefix={<Icon name="search" />}
                placeholder="搜索手机号"
                aria-label="搜索手机号"
                value={phoneInput}
                onChange={(e) => {
                  setPhoneInput(e.target.value);
                  if (!e.target.value && query.phone) applyPhone("");
                }}
                onPressEnter={() => applyPhone(phoneInput)}
              />
              <Button type="primary" icon={<Icon name="plus" />} onClick={openCreate}>
                新建账号
              </Button>
            </ToolbarGroup>
          </>
        }
        footer={<Pager total={total} current={query.page} pageSize={query.limit} onChange={(page, limit) => setQuery((prev) => ({ ...prev, page, limit }))} />}
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1040 }}
          locale={{
            emptyText: (
              <EmptyState
                icon="users"
                title={filtered ? "没有匹配的账号" : "还没有账号"}
                desc={filtered ? "换个手机号或角色试试，或者清空筛选条件。" : "新建第一个团队账号，并把邀请码发给成员。"}
                action={filtered ? <Button onClick={resetFilters}>清空筛选</Button> : <Button type="primary" onClick={openCreate}>新建账号</Button>}
              />
            ),
          }}
        />
      </Surface>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
        rootClassName="ddp-drawer"
        destroyOnClose
        title={
          <span className="ddp-drawer-title">
            <span className="ddp-drawer-title__row">
              {currentUser ? "编辑账号" : "新建账号"}
              {currentUser && <VocabBadge vocab={ROLE} value={currentUser.role} />}
            </span>
            <span className="ddp-drawer-title__sub">{currentUser ? `${currentUser.username} · ${currentUser.phone}` : "创建后会自动生成邀请码"}</span>
          </span>
        }
        footer={
          <DrawerFooter>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {currentUser ? "保存修改" : "创建账号"}
            </Button>
          </DrawerFooter>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSave} requiredMark>
          <Form.Item name="username" label="用户名" rules={[{ required: true, message: "请输入用户名" }]}>
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: "请输入手机号" },
              { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号" },
            ]}
          >
            <Input placeholder="请输入手机号" inputMode="numeric" maxLength={11} />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true, message: "请选择角色" }]}>
            <Select placeholder="请选择角色" options={roles} />
          </Form.Item>
          <Form.Item name="orgId" label="归属组织">
            <Select
              placeholder="请选择归属组织"
              allowClear
              showSearch
              optionFilterProp="label"
              options={orgs.map((org) => ({ value: org.id, label: org.name }))}
            />
          </Form.Item>
          <Form.Item name="email" label="邮箱" rules={[{ type: "email", message: "请输入正确的邮箱格式" }]}>
            <Input placeholder="请输入邮箱（可选）" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea placeholder="请输入备注（可选）" rows={3} />
          </Form.Item>
          <Form.Item name="isEnabled" label="账号状态" valuePropName="checked" extra="禁用后该账号无法登录三端。">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default UserList;
